import { useCallback, useEffect, useRef, useState } from 'react';
import { getMetaAdAccounts } from '../../services/metaAdsService';
import { getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { readMetaAdAccounts } from '../metaAdAccounts';

const INITIAL = {
  loaded: false,
  loading: false,
  refreshing: false,
  accounts: [],
  error: null,
  errorCode: null,
};

/**
 * Cuentas publicitarias del espacio (`GET /getmetaadaccounts`). Carga lo
 * guardado al montar (`enabled`); `refreshFromMeta` (solo dueño) usa `refresh=1`
 * y, si falla, deja la lista anterior con el error.
 *
 * @param {{ ownerUserId?: string|null, enabled?: boolean }} options
 *   `ownerUserId`: dueño del espacio ajeno (editor); `null` en el propio.
 */
export function useMetaAdAccounts({ ownerUserId = null, enabled = true } = {}) {
  const [state, setState] = useState(INITIAL);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  // `silent`: recarga de fondo (tras una sync); sin skeleton y, si falla, queda la lista.
  const load = useCallback(async ({ refresh = false, silent = false } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: !refresh && !silent, refreshing: refresh, error: silent ? prev.error : null, errorCode: silent ? prev.errorCode : null }));
    let res;
    try {
      res = await getMetaAdAccounts({ ownerUserId, refresh, signal: controller.signal });
    } catch (error) {
      // Cancelado (otro load lo reemplazó, se cerró el panel o StrictMode desmonta y
      // remonta en dev): no es un error ni se toca el estado. `safeRequest` solo deja
      // pasar AbortError; sin este catch quedaba como rechazo sin manejar (overlay de CRA).
      if (error?.name === 'AbortError') return null;
      throw error;
    }
    if (!mountedRef.current || controller.signal.aborted) return res;
    if (res?._httpOk) {
      setState({ ...INITIAL, loaded: true, accounts: readMetaAdAccounts(res) });
    } else if (silent) {
      setState((prev) => ({ ...prev, loading: false, refreshing: false }));
    } else {
      setState((prev) => ({
        ...prev,
        loaded: true,
        loading: false,
        refreshing: false,
        error: getMetaErrorMessage(res),
        errorCode: readMetaErrorCode(res) || null,
      }));
    }
    return res;
  }, [ownerUserId]);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  /** Lista que ya vino en otra respuesta (p. ej. `updatemetaadaccounts`). */
  const replaceAccounts = useCallback((res) => {
    setState({ ...INITIAL, loaded: true, accounts: readMetaAdAccounts(res) });
  }, []);

  const reload = useCallback(({ silent = false } = {}) => load({ silent }), [load]);
  const refreshFromMeta = useCallback(() => load({ refresh: true }), [load]);

  return { ...state, reload, refreshFromMeta, replaceAccounts };
}
